import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Footer } from "../components/SectionsD";

const Section = ({ num, title, children }) => (
  <section className="mb-12">
    <h2 className="flex items-baseline gap-4 mb-4">
      <span className="font-mono text-xs text-[#FFFFFF]">{num}</span>
      <span className="font-display uppercase tracking-[-0.02em] text-xl md:text-2xl text-[#D9E4F5]">{title}</span>
    </h2>
    <div className="pl-9 space-y-4 text-[15px] text-white/55 leading-[1.85]">{children}</div>
  </section>
);

const LegalLayout = ({ label, title, subtitle, children, testId }) => {
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-[#0E2A54] text-[#D9E4F5] min-h-screen grid-bg" data-testid={testId}>
      <div className="sticky top-0 z-40 border-b border-white/10 bg-[#091E42]/60 backdrop-blur-xl">
        <div className="max-w-[740px] mx-auto px-6 py-4">
          <Link
            to="/"
            data-testid="legal-back-home-link"
            className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] text-white/50 hover:text-[#FFFFFF] transition-colors duration-200"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> BACK TO HOME
          </Link>
        </div>
      </div>
      <article className="max-w-[740px] mx-auto px-6 pt-16 md:pt-24 pb-28">
        <p className="font-mono text-[11px] tracking-[0.3em] text-[#FFFFFF] mb-6">{label}</p>
        <h1 className="font-display uppercase tracking-[-0.03em] leading-[0.95] text-[clamp(34px,5vw,56px)] text-[#D9E4F5] mb-4">
          {title}
        </h1>
        <p className="text-sm text-white/45 leading-relaxed mb-16 border-b border-white/10 pb-10">{subtitle}</p>
        {children}
      </article>
      <Footer />
    </div>
  );
};

export const PrivacyPolicy = () => (
  <LegalLayout
    label="[ LEGAL — 01 ]"
    title="PRIVACY POLICY"
    subtitle={<>QandQinsurance (&ldquo;we,&rdquo; &ldquo;us,&rdquo; &ldquo;our&rdquo;) — Effective August 4, 2026</>}
    testId="privacy-policy-page"
  >
    <Section num="01" title="Overview">
      <p>
        This policy explains what information we collect through this website, why we collect it, and how it&rsquo;s used — in plain language.
      </p>
    </Section>
    <Section num="02" title="Information We Collect">
      <p>
        We collect your name, email, phone number, and any details you voluntarily submit through our call-booking or contact forms. We also collect standard technical data — IP address, browser type, device type, pages visited, and referring URL — via cookies and similar tracking technologies.
      </p>
    </Section>
    <Section num="03" title="How We Use Your Information">
      <p>
        We use your information to schedule your strategy call, send requested materials, follow up about the strategies and services you&rsquo;ve expressed interest in (including automated email and SMS messages you can opt out of at any time), improve our site, and comply with legal and regulatory recordkeeping obligations applicable to licensed insurance professionals.
      </p>
    </Section>
    <Section num="04" title="Cookies & Tracking Technologies">
      <p>
        We use the Meta (Facebook) Pixel to measure ad performance and build audiences for future advertising. You can disable cookies in your browser settings; this may affect site functionality.
      </p>
    </Section>
    <Section num="05" title="How We Share Your Information">
      <p>We do not sell your personal information. We may share it with:</p>
      <ul className="list-none space-y-3">
        <li><span className="text-[#FFFFFF] font-mono text-xs mr-2">(a)</span>service providers who help us operate this site and fulfill your request (e.g. our CRM platform, GoHighLevel);</li>
        <li><span className="text-[#FFFFFF] font-mono text-xs mr-2">(b)</span>insurance carriers and underwriters as needed to process any policy application you choose to initiate;</li>
        <li><span className="text-[#FFFFFF] font-mono text-xs mr-2">(c)</span>as required by law, regulation, or a valid legal process.</li>
      </ul>
    </Section>
    <Section num="06" title="TCPA Consent">
      <p>
        By submitting your phone number, you consent to receive calls and text messages from QandQinsurance and Najiyyah Qaasim, including by automated means, about debt-elimination strategies and insurance products and services. Consent is not a condition of any purchase. Message and data rates may apply. Reply STOP to opt out of texts at any time.
      </p>
    </Section>
    <Section num="07" title="Your Rights & Choices">
      <p>
        You may request access to, correction of, or deletion of your personal information by contacting us at najiyyahq@gmail.com. California residents have additional rights under the California Consumer Privacy Act (CCPA), including the right to know what personal information is collected, the right to request deletion, and the right to opt out of the sale of personal information (we do not sell personal information). To exercise these rights, contact us at najiyyahq@gmail.com.
      </p>
    </Section>
    <Section num="08" title="Data Security">
      <p>
        We use reasonable administrative, technical, and physical safeguards to protect your information, but no method of transmission or storage is 100% secure.
      </p>
    </Section>
    <Section num="09" title="Children's Privacy">
      <p>This site is not directed to children under 13, and we do not knowingly collect information from children.</p>
    </Section>
    <Section num="10" title="Changes to This Policy">
      <p>We may update this policy from time to time; the effective date above reflects the most recent revision.</p>
    </Section>
    <Section num="11" title="Contact Us">
      <p>
        QandQinsurance, 650 S. Shackleford Rd. Ste. 400, Little Rock, AR 72211 · najiyyahq@gmail.com · (718) 413-3927. Najiyyah Qaasim is licensed in Arkansas (#22055308), Tennessee (#3004021371), and Washington (#1362361).
      </p>
    </Section>
  </LegalLayout>
);

export const TermsPage = () => (
  <LegalLayout
    label="[ LEGAL — 02 ]"
    title="TERMS & CONDITIONS"
    subtitle="QandQinsurance — Effective August 4, 2026"
    testId="terms-page"
  >
    <Section num="01" title="Acceptance of Terms">
      <p>
        By accessing or using this website, you agree to these Terms &amp; Conditions. If you do not agree, do not use this site.
      </p>
    </Section>
    <Section num="02" title="No Financial or Insurance Advice">
      <p>
        Content on this site, including any strategy call, replay video, guide, or download, is for general educational purposes only. It is not personalized financial, tax, legal, or insurance advice, and is not a guarantee of any specific debt payoff timeline, savings amount, or policy outcome. Actual outcomes depend on individual policy structure, carrier terms, and consistent premium payment, and are subject to underwriting approval.
      </p>
    </Section>
    <Section num="03" title="Not an Insurance Application">
      <p>
        Booking a strategy call, downloading a guide, or submitting a contact form does not constitute an insurance policy application and does not obligate you to work with Najiyyah Qaasim or QandQinsurance.
      </p>
    </Section>
    <Section num="04" title="Licensing Disclosure">
      <p>
        Najiyyah Qaasim is a licensed insurance professional, licensed in Arkansas (#22055308), Tennessee (#3004021371), and Washington (#1362361). This site and any related content are directed only to residents of states in which Najiyyah Qaasim is licensed to transact insurance business; nothing on this site should be construed as an offer to sell insurance in any jurisdiction where such offer would be unlawful.
      </p>
    </Section>
    <Section num="05" title="Intellectual Property">
      <p>
        All content on this site (text, graphics, logos, videos) is owned by or licensed to QandQinsurance and may not be reproduced without written permission.
      </p>
    </Section>
    <Section num="06" title="No Warranty">
      <p>
        This site and its content are provided &ldquo;as is&rdquo; without warranties of any kind, express or implied, including accuracy, completeness, or fitness for a particular purpose.
      </p>
    </Section>
    <Section num="07" title="Limitation of Liability">
      <p>
        To the fullest extent permitted by law, QandQinsurance is not liable for any indirect, incidental, or consequential damages arising from your use of this site.
      </p>
    </Section>
    <Section num="08" title="Third-Party Links">
      <p>This site may link to third-party sites; we are not responsible for their content or privacy practices.</p>
    </Section>
    <Section num="09" title="Governing Law">
      <p>
        These terms are governed by the laws of the State of Arkansas, without regard to conflict-of-law principles.
      </p>
    </Section>
    <Section num="10" title="Changes to These Terms">
      <p>We may update these terms at any time; continued use of the site after changes constitutes acceptance.</p>
    </Section>
    <Section num="11" title="Contact Us">
      <p>
        QandQinsurance, 650 S. Shackleford Rd. Ste. 400, Little Rock, AR 72211 · najiyyahq@gmail.com.
      </p>
    </Section>
  </LegalLayout>
);
